import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';
import type { Vector3 } from '@kitware/vtk.js/types';

// Budget for a float32 3D texture, even if the driver's Int16 upload would
// otherwise be promoted to float. The three panoramic panes share this image.
export const PANORAMIC_TEXTURE_BYTES = 64 * 1024 * 1024;
export const PANORAMIC_TEXTURE_EDGE = 384;

export function panoramicTextureDimensions(
  dimensions: Vector3,
  maxTextureEdge: number,
  maxBytes = PANORAMIC_TEXTURE_BYTES
): Vector3 {
  if (!Number.isFinite(maxTextureEdge) || maxTextureEdge < 2 || maxBytes < 32)
    throw new Error('No hay una textura 3D disponible para la panorámica.');
  const edge = Math.min(PANORAMIC_TEXTURE_EDGE, Math.floor(maxTextureEdge));
  const voxels = dimensions.reduce((a, b) => a * b, 1);
  const scale = Math.min(
    1,
    edge / Math.max(...dimensions),
    Math.cbrt(maxBytes / (4 * voxels))
  );
  return dimensions.map((d) => Math.max(2, Math.floor(d * scale))) as Vector3;
}

function axisSamples(sourceSize: number, targetSize: number) {
  return Array.from({ length: targetSize }, (_, i) => {
    const position = Math.max(
      0,
      Math.min(sourceSize - 1, ((i + 0.5) * sourceSize) / targetSize - 0.5)
    );
    const lower = Math.floor(position);
    return {
      lower,
      upper: Math.min(sourceSize - 1, lower + 1),
      weight: position - lower,
    };
  });
}

/** Build only a display image; never replace or modify the cached study. */
export async function createPanoramicRenderImage(
  source: vtkImageData,
  maxTextureEdge: number,
  signal?: AbortSignal
): Promise<vtkImageData> {
  signal?.throwIfAborted();
  const dimensions = source.getDimensions();
  const target = panoramicTextureDimensions(dimensions, maxTextureEdge);
  if (target.every((d, i) => d === dimensions[i])) return source;
  const scalars = source.getPointData().getScalars();
  if (scalars.getNumberOfComponents() !== 1)
    throw new Error('Se requiere un volumen escalar.');
  const input = scalars.getData();
  const output = new Float32Array(target[0] * target[1] * target[2]);
  const [sx, sy] = dimensions;
  const [xSamples, ySamples, zSamples] = dimensions.map((d, i) =>
    axisSamples(d, target[i])
  );
  let cursor = 0;
  for (const [z, zs] of zSamples.entries()) {
    signal?.throwIfAborted();
    for (const ys of ySamples) {
      const row00 = sx * (ys.lower + sy * zs.lower);
      const row01 = sx * (ys.upper + sy * zs.lower);
      const row10 = sx * (ys.lower + sy * zs.upper);
      const row11 = sx * (ys.upper + sy * zs.upper);
      for (const xs of xSamples) {
        const a =
          Number(input[row00 + xs.lower]) * (1 - xs.weight) +
          Number(input[row00 + xs.upper]) * xs.weight;
        const b =
          Number(input[row01 + xs.lower]) * (1 - xs.weight) +
          Number(input[row01 + xs.upper]) * xs.weight;
        const c =
          Number(input[row10 + xs.lower]) * (1 - xs.weight) +
          Number(input[row10 + xs.upper]) * xs.weight;
        const d =
          Number(input[row11 + xs.lower]) * (1 - xs.weight) +
          Number(input[row11 + xs.upper]) * xs.weight;
        output[cursor++] =
          (a * (1 - ys.weight) + b * ys.weight) * (1 - zs.weight) +
          (c * (1 - ys.weight) + d * ys.weight) * zs.weight;
      }
    }
    if (z % 4 === 0)
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  signal?.throwIfAborted();
  const extent = source.getExtent();
  const ratios = dimensions.map((d, i) => d / target[i]);
  // Preserve voxel-edge bounds, including rotated directions and nonzero extents.
  const origin = source.indexToWorld(
    ratios.map((r, i) => extent[2 * i] + (r - 1) / 2) as Vector3
  );
  const image = vtkImageData.newInstance();
  image.setDimensions(target);
  image.setOrigin(Array.from(origin) as Vector3);
  image.setDirection(source.getDirection());
  image.setSpacing(source.getSpacing().map((s, i) => s * ratios[i]) as Vector3);
  image
    .getPointData()
    .setScalars(
      vtkDataArray.newInstance({ values: output, numberOfComponents: 1 })
    );
  return image;
}
